import { getAvailableThemes, type ThemeDefinition } from '../../../config/themes';
import { useThemeStore } from '../../../stores/themeStore';
import { useDevModeStore } from '../../../stores/devModeStore';

/** Preview colors for each theme */
const themePreviewColors: Record<string, { bg: string; accent: string; text: string }> = {
  default: { bg: '#f8fafc', accent: '#3b82f6', text: '#1e293b' },
  terminal: { bg: '#0a0a0a', accent: '#22c55e', text: '#22c55e' },
  industrial: { bg: '#1a1a1a', accent: '#f59e0b', text: '#fbbf24' },
  excel: { bg: '#ffffff', accent: '#217346', text: '#1f2937' },
  financial: { bg: '#1e293b', accent: '#3b82f6', text: '#f1f5f9' },
  retro: { bg: '#c0c0c0', accent: '#000080', text: '#000000' },
  macos: { bg: '#f5f5f5', accent: '#0066cc', text: '#1d1d1f' },
};

interface ThemeCardProps {
  theme: ThemeDefinition;
  isSelected: boolean;
  onSelect: () => void;
}

function ThemeCard({ theme, isSelected, onSelect }: ThemeCardProps) {
  const colors = themePreviewColors[theme.id] || themePreviewColors.default;

  return (
    <button
      onClick={onSelect}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors text-left w-full ${
        isSelected
          ? 'bg-theme-accent/10 border-theme-accent'
          : 'bg-theme-bg-tertiary border-theme-border hover:border-theme-accent/50'
      }`}
    >
      {/* Theme preview swatch */}
      <div
        className="w-10 h-10 rounded-md border border-theme-border flex-shrink-0 overflow-hidden"
        style={{ backgroundColor: colors.bg }}
      >
        <div className="h-2" style={{ backgroundColor: colors.accent }} />
        <div className="p-1">
          <div className="w-4 h-1 rounded-sm" style={{ backgroundColor: colors.text }} />
          <div className="w-3 h-1 rounded-sm mt-0.5" style={{ backgroundColor: colors.text, opacity: 0.5 }} />
        </div>
      </div>

      {/* Theme info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSelected ? 'text-theme-accent' : 'text-theme-text-primary'}`}>
            {theme.name}
          </span>
          {isSelected && (
            <span className="text-xs text-theme-accent">(Active)</span>
          )}
        </div>
        <p className="text-xs text-theme-text-muted truncate">{theme.description}</p>
      </div>

      {/* Selection indicator */}
      <div
        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
          isSelected ? 'border-theme-accent' : 'border-theme-border'
        }`}
      >
        {isSelected && <div className="w-2 h-2 rounded-full bg-theme-accent" />}
      </div>
    </button>
  );
}

export function ThemeSettings() {
  const currentTheme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const isDevMode = useDevModeStore((state) => state.settings.enabled);

  const availableThemes = getAvailableThemes(isDevMode);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
        Theme Settings
      </h3>

      <p className="text-xs text-theme-text-secondary">
        Choose a visual theme for the game interface.
        {isDevMode && (
          <span className="text-theme-warning ml-1">(Dev mode: showing all themes)</span>
        )}
      </p>

      <div className="grid gap-2">
        {availableThemes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isSelected={currentTheme === theme.id}
            onSelect={() => setTheme(theme.id)}
          />
        ))}
      </div>
    </div>
  );
}
